/* Horizontal carousels — native touch swipe plus desktop click-and-drag.

   The rails stay ordinary scroll-snap containers, so touchscreens and trackpads
   use browser-native physics. Mouse users can drag the row directly. Keyboard
   arrow keys remain available when the rail has focus. No visual arrow buttons
   are injected. */
(function () {
  const SELECTOR = '[data-carousel], .gr-rail, [data-gallery-main]';

  class GrCarousel {
    constructor(el) {
      this.el = el;
      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.build();
    }

    build() {
      if (this.el.dataset.grCarousel === 'on') return;
      this.el.dataset.grCarousel = 'on';
      this.el.setAttribute('tabindex', '0');
      this.el.setAttribute('role', this.el.getAttribute('role') || 'group');
      this.el.classList.add('gr-carousel');

      this.el.addEventListener('scroll', this.onScroll, { passive: true });
      this.el.addEventListener('keydown', this.onKeydown);
      this.el.addEventListener('pointerdown', this.onPointerDown);
      this.el.addEventListener('pointermove', this.onPointerMove, { passive: false });
      this.el.addEventListener('pointerup', this.onPointerUp);
      this.el.addEventListener('pointercancel', this.onPointerUp);
      this.el.addEventListener('lostpointercapture', this.onPointerUp);
      this.el.addEventListener('click', this.onClickCapture, true);
      this.el.addEventListener('dragstart', this.onDragStart);
      window.addEventListener('resize', this.onResize);

      this.sync();
    }

    destroy() {
      this.el.removeEventListener('scroll', this.onScroll);
      this.el.removeEventListener('keydown', this.onKeydown);
      this.el.removeEventListener('pointerdown', this.onPointerDown);
      this.el.removeEventListener('pointermove', this.onPointerMove);
      this.el.removeEventListener('pointerup', this.onPointerUp);
      this.el.removeEventListener('pointercancel', this.onPointerUp);
      this.el.removeEventListener('lostpointercapture', this.onPointerUp);
      this.el.removeEventListener('click', this.onClickCapture, true);
      this.el.removeEventListener('dragstart', this.onDragStart);
      window.removeEventListener('resize', this.onResize);
      this.el.classList.remove('gr-carousel', 'is-scrollable', 'is-dragging');
      delete this.el.dataset.grCarousel;
    }

    get overflowing() { return this.el.scrollWidth - this.el.clientWidth > 2; }
    get reduced() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

    get stepSize() {
      const first = this.el.firstElementChild;
      if (!first) return this.el.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(this.el).columnGap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    step(dir) {
      this.el.scrollBy({ left: this.stepSize * dir, behavior: this.reduced ? 'auto' : 'smooth' });
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !this.overflowing) return;
      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startScrollLeft = this.el.scrollLeft;
      this.didDrag = false;
      this.el.classList.add('is-dragging');
      this.el.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;
      const delta = event.clientX - this.startX;
      if (Math.abs(delta) > 5) this.didDrag = true;
      if (!this.didDrag) return;
      event.preventDefault();
      this.el.scrollLeft = this.startScrollLeft - delta;
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;
      const dragged = this.didDrag;
      if (this.el.hasPointerCapture(event.pointerId)) this.el.releasePointerCapture(event.pointerId);
      this.pointerId = null;
      this.el.classList.remove('is-dragging');

      if (!dragged) return;
      this.suppressClick = true;
      window.setTimeout(() => { this.suppressClick = false; }, 0);
      this.snapToClosest();
    }

    onClickCapture(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    onDragStart(event) {
      if (this.pointerId !== null && this.pointerId !== undefined) event.preventDefault();
    }

    snapToClosest() {
      const children = Array.from(this.el.children);
      if (!children.length) return;
      const max = this.el.scrollWidth - this.el.clientWidth;
      let best = this.el.scrollLeft;
      let distance = Infinity;

      children.forEach((child) => {
        const align = getComputedStyle(child).scrollSnapAlign;
        let target = child.offsetLeft;
        if (align.indexOf('center') !== -1) {
          target -= (this.el.clientWidth - child.offsetWidth) / 2;
        }
        target = Math.max(0, Math.min(max, target));
        const nextDistance = Math.abs(target - this.el.scrollLeft);
        if (nextDistance < distance) {
          best = target;
          distance = nextDistance;
        }
      });

      this.el.scrollTo({ left: best, behavior: this.reduced ? 'auto' : 'smooth' });
    }

    onScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.sync();
      });
    }

    onResize() { this.sync(); }

    sync() {
      this.el.classList.toggle('is-scrollable', this.overflowing);
    }
  }

  const instances = new WeakMap();

  function mount(root) {
    (root || document).querySelectorAll(SELECTOR).forEach((el) => {
      if (instances.has(el)) return;
      instances.set(el, new GrCarousel(el));
    });
  }

  function unmount(root) {
    (root || document).querySelectorAll(SELECTOR).forEach((el) => {
      const instance = instances.get(el);
      if (instance) {
        instance.destroy();
        instances.delete(el);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
  document.addEventListener('gr:cart:updated', () => mount());

  window.GrCarousel = GrCarousel;
})();
